import React, { useState } from "react";
import { districtList } from "./schoolDistrictsData";
import Footer from "../components/Footer";
import LandingSection from "../components/LandingSection";
import { Link } from "react-router-dom";
import ActionButton1 from "../utils/ActionButton1";
import schoolDistrictData from "./data/schoolDistrict.json";

const UtahSchools = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "county",
    direction: "asc",
  });
  const rowsPerPage = 15; // ✅ Adjust rows per page

  // Update districtList with new data from JSON
  const updatedDistrictList = districtList.map((district) => {
    const jsonDistrict = schoolDistrictData.school_districts.find(
      (sd) => sd.school_district === district.district,
    );
    if (jsonDistrict) {
      return {
        ...district,
        schools: jsonDistrict.public_schools + jsonDistrict.charter_schools,
        publicSchools: jsonDistrict.public_schools,
        charterSchools: jsonDistrict.charter_schools,
      };
    }
    return district;
  });

  // 🔍 Filter schools by search term
  const filteredData = updatedDistrictList.filter((school) =>
    Object.values(school)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  // 🔄 Sorting logic
  const sortedData = [...filteredData].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
    }
    return 0;
  });

  // 📄 Pagination logic
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + rowsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div>
      <LandingSection
        data={{
          imageName: "utahschools.webp",
          title: "Utah Schools",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah Schools – Empowering Minds, Shaping Futures</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah schools are dedicated to fostering academic excellence and
              innovation across diverse communities. With a strong focus on
              student development, they offer a supportive environment for
              learning and growth.
            </p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto w-full px-4 sm:px-8 lg:px-16 mb-[3rem]">
        <div className="text-center text-3xl sm:text-4xl lg:text-5xl font-bold mt-[5rem] mb-5">
          <h1 className="text-blue-900">School Districts in Utah</h1>
          <div className="mt-2 font-bold text-2xl text-[#CB0C0C]">
            ({schoolDistrictData.school_districts.length} Districts)
          </div>
        </div>

        {/* 🔍 Search box */}
        <div className="flex justify-center mb-6">
          <input
            type="text"
            placeholder="Search by district, county, or school count"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // reset page when searching
            }}
            className="border border-gray-400 rounded-lg px-4 py-2 w-[70%] md:w-[40%] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center justify-center my-12">
          <div className="bg-white rounded-xl shadow-[0_4px_12px_0_rgba(1,76,178,0.3)] overflow-hidden w-[95%]">
            <div className="overflow-x-auto ">
              <table className="table-auto border-collapse border bg-white border-gray-300 w-full">
                <thead className="sticky top-0 z-10 rounded-lg border border-pink-700 bg-red-200/50">
                  <tr>
                    {[
                      // { label: "S.no", key: "sn" }, // Not sortable
                      { label: "NCES ID", key: "ncesId" },
                      { label: "State ID", key: "stateId" },
                      { label: "School District", key: "district" },
                      {
                        label: "Public Schools",
                        key: "publicSchools",
                        sortable: true,
                      },
                      {
                        label: "Charter Schools",
                        key: "charterSchools",
                        sortable: true,
                      },
                      {
                        label: "Total Schools",
                        key: "schools",
                        sortable: true,
                      },
                      { label: "County Name", key: "county", sortable: true },
                    ].map((col, i) => (
                      <th
                        key={i}
                        onClick={() => col.sortable && requestSort(col.key)}
                        className={`px-6 py-4 text-center text-[18px] font-bold text-blue-800 tracking-wider border border-gray-300 ${
                          col.sortable ? "cursor-pointer hover:bg-red-300" : ""
                        }`}
                      >
                        {col.label}
                        {col.sortable && sortConfig.key === col.key
                          ? sortConfig.direction === "asc"
                            ? " ▲"
                            : " ▼"
                          : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((school, index) => (
                    <React.Fragment key={school.stateId}>
                      {(index === 0 ||
                        paginatedData[index - 1].county !== school.county) && (
                        <tr className="bg-gray-100">
                          <td
                            colSpan="7"
                            className="text-center font-bold py-2 text-gray-800"
                          >
                            {school.county} County (
                            {
                              filteredData.filter(
                                (d) => d.county === school.county,
                              ).length
                            }{" "}
                            {filteredData.filter(
                              (d) => d.county === school.county,
                            ).length === 1
                              ? "District"
                              : "Districts"}
                            )
                          </td>
                        </tr>
                      )}
                      <tr key={index} className="hover:bg-gray-100">
                        {/* <td className="border border-gray-300 px-4 py-2 text-center">
                          
                          {startIndex + index + 1}
                        </td> */}
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {school.ncesId}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {school.stateId}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 flex items-center">
                          <img
                            src={school.logo}
                            alt={school.district}
                            className="w-[6rem] h-auto mx-[2rem]"
                          />
                          <Link
                            to={`/utah-at-a-glance/universities-and-schools/utah-schools/${school.districtSlug}`}
                            className="text-blue-600 underline hover:text-blue-800"
                          >
                            {school.district}
                          </Link>
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {school.publicSchools}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {school.charterSchools}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {school.schools}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {school.county}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  {paginatedData.length === 0 && (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-6 text-gray-500"
                      >
                        No matching records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* 📄 Pagination */}
            <div className="flex justify-center items-center py-4 gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span className="px-4 py-2 text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      <Link
        to="/utah-at-a-glance/universities-and-schools/utah-schools/charter-schools"
        // target="_blank"
        rel="noopener noreferrer"
        className="block w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg mx-auto text-center"
      >
        <div className="w-full">
          <ActionButton1 data={"List of Chartered Schools"} />
        </div>
      </Link>
      {/* Data Sources */}
      <div className="mt-8 mb-8 border-t border-gray-300 pt-6 text-center text-sm text-gray-700 max-w-3xl mx-auto px-4">
        <p className="font-semibold mb-3">Data Sources:</p>
        <div className="flex flex-col items-center gap-2">
          <a
            target="_blank"
            href="https://schools.utah.gov/schoolsdirectory"
            rel="noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Utah State Board of Education - Schools Directory
          </a>
          <a
            target="_blank"
            href="https://www.uen.org/institutions/districts"
            rel="noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Utah Education Network - Districts
          </a>
          <a
            target="_blank"
            href="https://reportcard.schools.utah.gov/"
            rel="noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Utah State Board of Education - Report Card
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UtahSchools;
